import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Wrench, Gift, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { formatCurrency } from "@/lib/currency";

interface StarterMaterialsTabProps {
  starterMaterials: any[] | undefined;
  materialsLoading: boolean;
  starterMaterialCategories: any[];
  currency: string;
  isAdmin: boolean;
  materialPage: number;
  setMaterialPage: (page: number) => void;
  materialSortField: string;
  setMaterialSortField: (field: string) => void;
  materialSortDirection: "asc" | "desc";
  setMaterialSortDirection: (dir: "asc" | "desc") => void;
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  importAllMaterialsMutation: any;
  deleteMaterialMutation: any;
  materialSearchQuery: string;
  setMaterialSearchQuery: (query: string) => void;
  onAdd?: () => void;
  onEdit?: (material: any) => void;
  onDelete?: (id: string) => void;
  onUpload?: () => void;
  onView?: (material: any) => void;
}

export function StarterMaterialsTab({
  starterMaterials,
  materialsLoading,
  starterMaterialCategories,
  currency,
  isAdmin,
  materialPage,
  setMaterialPage,
  materialSortField,
  setMaterialSortField,
  materialSortDirection,
  setMaterialSortDirection,
  itemsPerPage,
  setItemsPerPage,
  importAllMaterialsMutation,
  deleteMaterialMutation,
  materialSearchQuery,
  setMaterialSearchQuery,
  onAdd,
  onEdit,
  onDelete,
  onUpload,
  onView,
}: StarterMaterialsTabProps) {

  const getColorFromName = (name: string) => {
    const colors = [
      'bg-red-200', 'bg-blue-200', 'bg-green-200', 'bg-yellow-200',
      'bg-purple-200', 'bg-pink-200', 'bg-indigo-200', 'bg-orange-200'
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const sortedMaterials = useMemo(() => {
    if (!starterMaterials) return [];

    const filtered = starterMaterials.filter(mat =>
      mat.name.toLowerCase().includes(materialSearchQuery.toLowerCase()) ||
      (starterMaterialCategories?.find(c => c._id === mat.categoryId)?.name || mat.category || '').toLowerCase().includes(materialSearchQuery.toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      let aVal = a[materialSortField as keyof typeof a];
      let bVal = b[materialSortField as keyof typeof b];

      if (materialSortField === 'category') {
        aVal = starterMaterialCategories?.find(c => c._id === a.categoryId)?.name || a.category || '';
        bVal = starterMaterialCategories?.find(c => c._id === b.categoryId)?.name || b.category || '';
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return materialSortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      const aNum = parseFloat(aVal as string) || 0;
      const bNum = parseFloat(bVal as string) || 0;
      return materialSortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return sorted;
  }, [starterMaterials, materialSortField, materialSortDirection, starterMaterialCategories, materialSearchQuery]);

  const paginatedMaterials = useMemo(() => {
    if (!sortedMaterials) return [];
    const start = (materialPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedMaterials.slice(start, end);
  }, [sortedMaterials, materialPage, itemsPerPage]);

  const materialTotalPages = Math.max(1, Math.ceil((sortedMaterials?.length || 0) / itemsPerPage));

  const handleSort = (field: string) => {
    if (materialSortField === field) {
      setMaterialSortDirection(materialSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMaterialSortField(field);
      setMaterialSortDirection('asc');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 space-y-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle>Starter Materials ({sortedMaterials?.length || 0})</CardTitle>
          <CardDescription>Template materials and equipment for new users</CardDescription>
        </div>
        <div className="flex gap-2">
          {isAdmin && onAdd && (
            <>
              <Button onClick={onAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Material
              </Button>
              {onUpload && (
                <Button variant="outline" onClick={onUpload}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload CSV
                </Button>
              )}
            </>
          )}
        </div>
        </div>
        <Input
            placeholder="Search materials..."
            value={materialSearchQuery}
            onChange={(e) => setMaterialSearchQuery(e.target.value)}
            className="w-full"
          />
      </CardHeader>
      <CardContent>
        {materialsLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('name')}
                  >
                    Name {materialSortField === 'name' && (materialSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('category')}
                  >
                    Category {materialSortField === 'category' && (materialSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('purchaseAmount')}
                  >
                    Purchase Amount {materialSortField === 'purchaseAmount' && (materialSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead
                    className="text-right cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('pricePerUnit')}
                  >
                    Price/Unit {materialSortField === 'pricePerUnit' && (materialSortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  {isAdmin && (onEdit || onDelete) && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedMaterials.map((item) => (
                  <TableRow
                    key={item._id || item.id}
                    onClick={() => onView?.(item)}
                    className={onView ? "cursor-pointer hover:bg-muted/50" : ""}
                  >
                    <TableCell>
                      {(item.image || item.imageUrl) ? (
                        <img src={item.image || item.imageUrl} alt={item.name} className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div className={`w-10 h-10 ${getColorFromName(item.name)} rounded flex items-center justify-center text-xs font-semibold text-gray-700`}>
                          {item.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{starterMaterialCategories?.find(c => c._id === item.categoryId)?.name || item.category || "-"}</TableCell>
                    <TableCell className="text-right">{item.quantity || "-"}</TableCell>
                    <TableCell>{item.unit || "-"}</TableCell>
                    <TableCell className="text-right">
                      {item.purchaseAmount ? formatCurrency(item.purchaseAmount, currency) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.pricePerUnit ? formatCurrency(item.pricePerUnit, currency) : "-"}
                    </TableCell>
                    {isAdmin && (onEdit || onDelete) && (
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2">
                          {onEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onEdit(item)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {onDelete && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Material</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{item.name}"? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => onDelete(item._id || item.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {materialTotalPages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={materialPage}
                  totalPages={materialTotalPages}
                  totalItems={sortedMaterials?.length || 0}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setMaterialPage}
                  onPageSizeChange={setItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
